// src/providers/ThemeProvider.tsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "../config/theme";

type ThemeMode = "light" | "dark" | "auto";

export const accentColors: Record<
  string,
  { title: string; dark: string; light: string }
> = {
  blue: { title: "ฟ้า", dark: "#016BB6", light: "#016BB6" },
  red: { title: "แดง", dark: "#D32F2F", light: "#C62828" },
  green: { title: "เขียว", dark: "#388E3C", light: "#2C6E2F" },
  purple: { title: "ม่วง", dark: "#9F42BB", light: "#7B1FA2" },
  orange: { title: "ส้ม", dark: "#F57C00", light: "#FF7043" },
  yellow: { title: "เหลือง", dark: "#FBC02D", light: "#F9A825" },
};

interface ThemeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  accentColor: string;
  setAccentColor: (color: string) => void;
}

export const ThemeContext = createContext<ThemeContextType | null>(null);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const getInitialThemeMode = (): ThemeMode => {
    const storedThemeMode = sessionStorage.getItem("themeMode") as ThemeMode;

    if (
      storedThemeMode === "light" ||
      storedThemeMode === "dark" ||
      storedThemeMode === "auto"
    ) {
      return storedThemeMode;
    }

    return "auto";
  };

  const getInitialAccentColor = (): string => {
    return sessionStorage.getItem("accentColor") || "blue";
  };

  const [themeMode, setThemeMode] = useState<ThemeMode>(getInitialThemeMode);
  const [accentColor, setAccentColor] = useState<string>(getInitialAccentColor);

  useEffect(() => {
    sessionStorage.setItem("themeMode", themeMode);
  }, [themeMode]);

  useEffect(() => {
    sessionStorage.setItem("accentColor", accentColor);
  }, [accentColor]);

  const [effectiveThemeMode, setEffectiveThemeMode] = useState<
    "light" | "dark"
  >(() => {
    const prefersDarkScheme = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;
    return prefersDarkScheme ? "dark" : "light";
  });

  useEffect(() => {
    const updateEffectiveThemeMode = () => {
      if (themeMode === "auto") {
        const prefersDarkScheme = window.matchMedia(
          "(prefers-color-scheme: dark)"
        ).matches;
        setEffectiveThemeMode(prefersDarkScheme ? "dark" : "light");
      } else {
        setEffectiveThemeMode(themeMode);
      }
    };

    updateEffectiveThemeMode();

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const mediaQueryListener = (event: MediaQueryListEvent) => {
      if (themeMode === "auto") {
        setEffectiveThemeMode(event.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", mediaQueryListener);

    return () => {
      mediaQuery.removeEventListener("change", mediaQueryListener);
    };
  }, [themeMode]);

  // Create the theme using the selected mode and accent color
  const theme = createAppTheme(effectiveThemeMode, accentColors[accentColor]);

  return (
    <ThemeContext.Provider
      value={{ themeMode, setThemeMode, accentColor, setAccentColor }}
    >
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
